import { motion } from "motion/react";
import { bool, func, number, object, string } from "prop-types";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import { fullScreenListItemWidth } from "NavBar/styleConstants";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrInput3 from "UI/FnrInput3";
import DefaultIcon from "UI/FnrInput3/Decorators/DefaultIcon";
import { standardInputStyles } from "UI/FnrInput3/Inputs/DefaultInput";
import FnrSlides from "UI/FnrSlides";
import FnrTypography from "UI/FnrTypography";
import { colors, colorsByFunction } from "UI/colors";

import { addOpacity, lightenDarkenColor } from "Utilities/Colors/colorUtils";
import useHasFinePointer from "Utilities/Hooks/useHasFinePointer";
import useHeight from "Utilities/Hooks/useHeight";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import useWidth from "Utilities/Hooks/useWidth";
import dynTop from "Utilities/interpolations/dynTop";
import stopEvents from "Utilities/stopEvents";

import {
  InnerWrapper,
  floatingButtonStyles,
  iconPaddingRight,
} from "./ButtonStyles";
import IconWrapper from "./IconWrapper";

const SearchInputContext = createContext();
const BaseSearchButtonContext = createContext();

const useSearchInputContext = () => useContext(SearchInputContext);
const useBaseSearchButtonContext = () => useContext(BaseSearchButtonContext);

const SearchContainer = styled.div`
  ${floatingButtonStyles}
  background-color: ${(props) =>
    props.$isActive
      ? props.$activeBackgroundColor
      : props.$inactiveBackgroundColor};
  transition: background-color 200ms;
`;

const ButtonWrapper = styled(motion.div)`
  display: flex;
`;

const SDefaultInput = styled(motion.input)`
  ${standardInputStyles}
  width: auto; /* Override width: 100% from standardInputStyles to allow motion animation */
  border-radius: 20px;
  height: 30px;
  font-size: small;
  padding-left: 28px;
  padding-right: 28px;
  border: none;
  background-color: ${colors.transparent};
  caret-color: ${colorsByFunction.textLight};
  color: ${colorsByFunction.textLight};
  &::placeholder {
    color: ${lightenDarkenColor(colorsByFunction.textSecondaryLight, 40)};
  }
  &:focus,
  &:hover {
    box-shadow: ${(props) => {
      if (!props.$glowColor) return "none";
      const color = props.theme?.colors?.[props.$glowColor] || props.$glowColor;
      return `0 0 0 1px ${color}`;
    }};
  }
`;

const Input = forwardRef(
  ({ glowColor, initial, animate, transition, ...props }, ref) => {
    return (
      <SDefaultInput
        {...props}
        $glowColor={glowColor}
        initial={initial}
        animate={animate}
        transition={transition}
        ref={ref}
      />
    );
  }
);

Input.propTypes = {
  glowColor: string,
  initial: object,
  animate: object,
  transition: object,
};

const SearchInput = forwardRef(
  (
    {
      isActive,
      searchValue,
      onSearchChange,
      onClearSearch,
      setIsActive,
      placeholder,
      dataCyInput,
      dataCyCloseButton,
      buttonWidth,
      ...restProps
    },
    ref
  ) => {
    const finalWidth = fullScreenListItemWidth - 88;

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Escape") {
          onClearSearch();
          setIsActive(false);
          stopEvents(e);
        }
      },
      [onClearSearch, setIsActive]
    );
    const contextValue = useMemo(
      () => ({ onClearSearch, setIsActive, dataCyCloseButton }),
      [onClearSearch, setIsActive, dataCyCloseButton]
    );

    return (
      <SearchInputContext.Provider value={contextValue}>
        <Padding>
          <FnrInput3
            ref={ref}
            Component={Input}
            value={searchValue}
            setValue={onSearchChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            PrefixComponent={InputIcon}
            SuffixComponent={CloseButton}
            icon="search far"
            autoComplete="off"
            data-cy={dataCyInput}
            initial={{ width: buttonWidth }}
            animate={{ width: isActive ? finalWidth : buttonWidth }}
            transition={{
              ease: "anticipate",
              duration: 0.3,
            }}
            {...restProps}
          />
        </Padding>
      </SearchInputContext.Provider>
    );
  }
);

const Padding = styled.div`
  padding: 1px;
`;

const TextWrapper = styled.div`
  padding-right: 4px;
`;

SearchInput.propTypes = {
  isActive: bool,
  searchValue: string,
  onSearchChange: func,
  onClearSearch: func,
  setIsActive: func,
  placeholder: string,
  dataCyInput: string,
  dataCyCloseButton: string,
  buttonWidth: number,
};

const CloseButton = forwardRef(({ inputRef, ...props }, ref) => {
  const { onClearSearch, setIsActive, dataCyCloseButton } =
    useSearchInputContext();
  const { activeBackgroundColor } = useBaseSearchButtonContext();
  const inputHeight = useHeight(inputRef);
  const isHoverFocused = useIsHoverFocused(ref);

  const handleClick = () => {
    onClearSearch();
    setIsActive(false);
  };

  return (
    <SFnrButtonWrapper
      $top={inputHeight ? (inputHeight - 2) / 2 : "50%"}
      onClick={handleClick}
      ref={ref}
      data-cy={dataCyCloseButton}
      {...props}
    >
      <SFnrContainedIcon
        backgroundColor={
          isHoverFocused ? addOpacity(colors.white, 0.8) : colors.white
        }
        color={activeBackgroundColor}
        size="sm"
        icon="fa-times fas"
        noAnimation
      />
    </SFnrButtonWrapper>
  );
});

CloseButton.propTypes = {
  inputRef: object,
};

const SFnrContainedIcon = styled(FnrContainedIcon)`
  border-radius: 50%;
`;

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  position: absolute;
  right: 3.5px;
  transform: translateY(-50%);
  border-radius: 50%;
  ${dynTop}
`;

const SDefaultIcon = styled(DefaultIcon)`
  color: ${colorsByFunction.textLight};
  font-size: small;
  transform: translate(-4px, calc(-50%));
`;

const InputIcon = forwardRef((props, ref) => {
  return <SDefaultIcon ref={ref} {...props} />;
});

const Button = forwardRef(
  ({ onOpen, dataCySearchButton, initial, animate, transition }, ref) => {
    return (
      <ButtonWrapper
        initial={initial}
        animate={animate}
        transition={transition}
      >
        <FnrShineButton
          onClick={onOpen}
          data-cy={dataCySearchButton}
          ref={ref}
        >
          <InnerWrapper>
            <IconWrapper $paddingRight={iconPaddingRight}>
              <FnrContainedIcon
                icon="search far"
                size="sm"
                color="textLight"
                backgroundColor="transparent"
                noPadding
              />
            </IconWrapper>
            <TextWrapper>
              <FnrTypography
                fontSize="13px"
                lineHeight={1}
                color="textLight"
                shadow
                noWrap
              >
                SEARCH
              </FnrTypography>
            </TextWrapper>
          </InnerWrapper>
        </FnrShineButton>
      </ButtonWrapper>
    );
  }
);

Button.propTypes = {
  onOpen: func,
  dataCySearchButton: string,
  inactiveBackgroundColor: string,
  initial: object,
  animate: object,
  transition: object,
};

const InputComponent = (props) => {
  const {
    inputRef,
    isActive,
    buttonWidth,
    searchValue,
    onSearchChange,
    onClearSearch,
    setIsActive,
    placeholder,
    dataCyInput,
    dataCyCloseButton,
  } = useBaseSearchButtonContext();

  return (
    <SearchInput
      ref={inputRef}
      isActive={isActive}
      buttonWidth={buttonWidth}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      onClearSearch={onClearSearch}
      setIsActive={setIsActive}
      placeholder={placeholder}
      dataCyInput={dataCyInput}
      dataCyCloseButton={dataCyCloseButton}
      {...props}
    />
  );
};

const ButtonComponent = (props) => {
  const {
    buttonRef,
    inputRef,
    isActive,
    buttonWidth,
    setIsActive,
    dataCySearchButton,
    inactiveBackgroundColor,
  } = useBaseSearchButtonContext();

  const handleOpen = useCallback(() => {
    setIsActive(true);
    // Focus synchronously in the tap handler so iOS shows the keyboard
    inputRef.current?.focus();
  }, [setIsActive, inputRef]);

  const finalWidth = fullScreenListItemWidth - 88;

  return (
    <Button
      ref={buttonRef}
      onOpen={handleOpen}
      dataCySearchButton={dataCySearchButton}
      inactiveBackgroundColor={inactiveBackgroundColor}
      initial={{ width: finalWidth }}
      animate={{ width: isActive ? finalWidth : buttonWidth }}
      transition={{
        ease: "anticipate",
        duration: 0.3,
      }}
      {...props}
    />
  );
};

const COMPONENTS = [
  {
    key: "input",
    value: InputComponent,
    keepInDOM: true,
  },
  {
    key: "button",
    value: ButtonComponent,
    keepInDOM: true,
  },
];

const BaseSearchButton = ({
  isActive,
  setIsActive,
  searchValue,
  onSearchChange,
  onClearSearch,
  placeholder,
  dataCyInput,
  dataCyCloseButton,
  dataCySearchButton,
  inactiveBackgroundColor,
  activeBackgroundColor,
  glowColor,
}) => {
  const inputRef = useRef();
  const buttonRef = useRef();
  const buttonWidth = useWidth(buttonRef);
  const stableButtonWidthRef = useRef(0);

  // Store stable buttonWidth to use during exit animation
  useEffect(() => {
    if (buttonWidth > 0) {
      stableButtonWidthRef.current = buttonWidth;
    }
  }, [buttonWidth]);

  useEffect(() => {
    if (!isActive) {
      setTimeout(() => {
        buttonRef.current?.focus();
      });
    }
  }, [isActive]);

  // On touch devices, scroll the input into view after the keyboard appears,
  // then restore scroll position when closing.
  const [oldScrollY, setOldScrollY] = useState();
  const hasFinePointer = useHasFinePointer();
  useEffect(() => {
    if (hasFinePointer) return;
    if (isActive) {
      setOldScrollY(window.scrollY);
      setTimeout(() => {
        if (inputRef.current) {
          const inputRect = inputRef.current.getBoundingClientRect();
          const visibleHeight =
            window.visualViewport?.height || window.innerHeight;
          window.scrollTo({
            top: window.scrollY + inputRect.bottom - visibleHeight,
            behavior: "smooth",
          });
        }
      }, 300);
    } else if (oldScrollY !== undefined) {
      setTimeout(() => {
        window.scrollTo({
          top: oldScrollY,
          behavior: "smooth",
        });
      });
    }
  }, [isActive, hasFinePointer]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = useMemo(
    () => ({
      inputRef,
      buttonRef,
      isActive,
      buttonWidth: buttonWidth || stableButtonWidthRef.current,
      searchValue,
      onSearchChange,
      onClearSearch,
      setIsActive,
      placeholder,
      dataCyInput,
      dataCyCloseButton,
      dataCySearchButton,
      inactiveBackgroundColor,
      activeBackgroundColor,
    }),
    [
      isActive,
      searchValue,
      onSearchChange,
      onClearSearch,
      setIsActive,
      placeholder,
      dataCyInput,
      dataCyCloseButton,
      dataCySearchButton,
      inactiveBackgroundColor,
      buttonWidth,
      activeBackgroundColor,
    ]
  );

  const activeKey = isActive ? "input" : "button";

  return (
    <BaseSearchButtonContext.Provider value={contextValue}>
      <SearchContainer
        $isActive={isActive}
        $inactiveBackgroundColor={inactiveBackgroundColor}
        $activeBackgroundColor={activeBackgroundColor}
        $noGlow
      >
        <FnrSlides
          variant="fade"
          activeKey={activeKey}
          components={COMPONENTS}
          clip={false}
          glowColor={glowColor}
          fullWidth
        />
      </SearchContainer>
    </BaseSearchButtonContext.Provider>
  );
};

BaseSearchButton.propTypes = {
  isActive: bool,
  setIsActive: func,
  searchValue: string,
  onSearchChange: func,
  onClearSearch: func,
  placeholder: string,
  dataCyInput: string,
  dataCyCloseButton: string,
  dataCySearchButton: string,
  inactiveBackgroundColor: string,
  glowColor: string,
  activeBackgroundColor: string,
};

export { useSearchInputContext };
export default BaseSearchButton;
